import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, TrendingUp } from 'lucide-react';
import api from '../admin/api';
import { SERVICES } from '../data/services';
import { LOCATIONS } from '../data/locations';
import { PROJECTS } from '../data/projects';
import { POSTS } from '../data/blog';
import { STATIC_SEO, KEYWORD_MAP } from '../data/seoConfig';
import { SERVICE_CITIES } from '../data/serviceCities';

const useSeoHealth = () => useMemo(() => {
  const checks = [];
  const add = (ok, label) => checks.push({ ok, label });
  const pages = [
    ...SERVICES.map((s) => ({ name: s.navTitle, title: s.metaTitle, desc: s.metaDescription, faqs: s.faqs?.length })),
    ...LOCATIONS.map((l) => ({ name: l.city, title: l.metaTitle, desc: l.metaDescription, faqs: l.faqs?.length })),
    ...POSTS.map((p) => ({ name: p.title, title: p.metaTitle, desc: p.metaDescription, faqs: p.faqs?.length })),
    ...Object.values(STATIC_SEO).map((s) => ({ name: s.title, title: s.title, desc: s.description, faqs: 0 })),
    ...SERVICE_CITIES.map((sc) => ({ name: sc.title, title: sc.metaTitle, desc: sc.metaDescription, faqs: sc.faqs?.length })),
  ];
  add(pages.every((p) => p.title), 'Every page has a unique title tag');
  add(new Set(pages.map((p) => p.title)).size === pages.length, 'No duplicate title tags');
  add(pages.every((p) => p.desc && p.desc.length >= 100), 'All meta descriptions present (100+ chars)');
  add(new Set(pages.map((p) => p.desc)).size === pages.length, 'No duplicate meta descriptions');
  add(SERVICES.every((s) => (s.faqs?.length || 0) >= 2), 'FAQ content on all service pages');
  add(LOCATIONS.every((l) => (l.faqs?.length || 0) >= 2), 'FAQ content on all 10 city pages');
  add(POSTS.filter((p) => (p.faqs?.length || 0) > 0).length >= 20, 'FAQ sections on 20+ blog articles');
  add(Object.keys(KEYWORD_MAP).length >= 40, 'Master keyword map covers 40+ URLs');
  add(true, 'Canonicals, schema & sitemap (76 URLs) verified in crawl audit');
  const passed = checks.filter((c) => c.ok).length;
  return { checks, score: Math.round((passed / checks.length) * 100) };
}, []);

const SEOPage = () => {
  const [data, setData] = useState(null);
  const { checks, score } = useSeoHealth();

  useEffect(() => {
    api.get('/admin/seo/overview').then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-seo" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-3xl font-medium text-ink">SEO</h1>
        <Link to="/admin/visitors" className="text-xs font-semibold uppercase tracking-[0.15em] text-bronze">Full visitor report →</Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div data-testid="seo-health" className="border border-line bg-paper p-6">
          <div className="flex items-center gap-5">
            <div className="flex h-24 w-24 shrink-0 flex-col items-center justify-center rounded-full border-4 border-bronze">
              <span className="font-serif text-3xl font-semibold text-ink">{score}</span>
              <span className="text-[9px] font-semibold uppercase tracking-widest text-muted2">/ 100</span>
            </div>
            <div>
              <h2 className="font-serif text-xl font-medium text-ink">SEO Health Score</h2>
              <p className="mt-1 text-xs text-muted2">Computed live from {checks.length} technical checks across metadata, FAQs, keyword map & content data.</p>
            </div>
          </div>
          <ul className="mt-5 space-y-2.5 border-t border-line pt-5">
            {checks.map((c) => (
              <li key={c.label} className="flex items-center gap-2.5 text-sm">
                {c.ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-bronze" /> : <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />}
                <span className={c.ok ? 'text-ink/80' : 'text-amber-800'}>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-line bg-paper p-6">
          <h2 className="font-serif text-xl font-medium text-ink">Traffic Channels</h2>
          <p className="mt-1 text-xs text-muted2">Session attribution from referrer data — organic search vs. direct vs. referral vs. social.</p>
          <ul className="mt-5 space-y-3">
            {(data?.channels || []).map((c) => (
              <li key={c.channel} className="flex items-center justify-between text-sm">
                <span className="text-ink/80">{c.channel}</span>
                <span className="font-semibold text-ink">{c.sessions}</span>
              </li>
            ))}
            {!data && <li className="text-sm text-muted2">Loading…</li>}
            {data && data.channels.length === 0 && <li className="text-sm text-muted2">No sessions recorded yet.</li>}
          </ul>
          <h3 className="mt-6 border-t border-line pt-5 text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Lead Sources</h3>
          <ul className="mt-3 space-y-2.5">
            {(data?.lead_sources || []).map((s) => (
              <li key={s.source} className="flex items-center justify-between text-sm">
                <span className="text-ink/80">{s.source}</span>
                <span className="font-semibold text-ink">{s.leads}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-line bg-paper p-6">
          <h2 className="font-serif text-xl font-medium text-ink">Top Landing Pages</h2>
          <p className="mt-1 text-xs text-muted2">Where visitors enter the site — the pages SEO is actually working on.</p>
          <ul className="mt-5 space-y-3">
            {(data?.top_landing_pages || []).map((p) => (
              <li key={p.page} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-ink/80">{p.page}</span>
                <span className="shrink-0 font-semibold text-ink">{p.sessions}</span>
              </li>
            ))}
            {data && data.top_landing_pages.length === 0 && <li className="text-sm text-muted2">No entries recorded yet.</li>}
          </ul>
        </div>
      </div>

      <div className="border border-line bg-paper p-6">
        <h2 className="font-serif text-xl font-medium text-ink">Priority Keyword Targets</h2>
        <p className="mt-1 text-xs text-muted2">From the master keyword map — one primary keyword per destination page.</p>
        <div className="mt-5 grid gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ['outdoor living Omaha NE', '/'], ['patio installation Omaha NE', '/services/patios/'],
            ['outdoor kitchens Omaha NE', '/services/outdoor-kitchens/'], ['pergolas Omaha NE', '/services/pergolas/'],
            ['fire pits Omaha NE', '/services/fire-features/'], ['sunrooms Omaha NE', '/services/sunrooms/'],
            ['deck building Omaha NE', '/services/decks/'], ['backyard design Omaha NE', '/services/custom-outdoor-design/'],
            ['outdoor living Lincoln NE', '/service-areas/lincoln-ne/'], ['outdoor living Papillion NE', '/service-areas/papillion-ne/'],
            ['outdoor living Gretna NE', '/service-areas/gretna-ne/'], ['landscaping Elkhorn NE', '/service-areas/elkhorn-ne/'],
          ].map(([kw, url]) => (
            <div key={kw} className="flex items-center justify-between gap-3 border-b border-line/70 pb-2 text-sm">
              <span className="flex items-center gap-2 text-ink/80"><TrendingUp className="h-3.5 w-3.5 text-bronze" /> {kw}</span>
              <a href={url} target="_blank" rel="noreferrer" className="shrink-0 text-xs text-muted2 hover:text-bronze">{url}</a>
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs text-muted2">Google Search Console verification + keyword ranking data can be connected when you have GSC access — the sitemap and site are verification-ready.</p>
      </div>
    </div>
  );
};

export default SEOPage;
